// First-run welcome card (desktop only — installed alongside the desktop UI,
// never on the VR path). Shown once on first run: dismissal persists
// `welcomeSeen` via ui/settings-store, so any dismissal IS "don't show again".
// It can also be re-opened on demand from the account menu ("Show welcome
// intro") — installWelcome returns a `show()` handle for that.
//
// Deliberately NON-blocking, unlike ui/modal.ts's dialogs: the backdrop layer
// has pointer-events:none (only the card itself is interactive), and the card
// dismisses itself on the FIRST interaction anywhere else — a pointerdown
// outside the card, or any non-modifier key. A brand-new user reads it and
// clicks "Get started"; anyone who just starts working never fights an
// overlay (and the headless smoke test's first toolbar click dismisses it
// without special-casing). Esc / Enter also dismiss. DOM-only: no scene
// change, so no wakeRendering needed.
//
// Styling follows ui/modal.ts's .dt-modal card look (own class prefix).

import { keyForAction } from "./hotkeys";
import { loadSettings, saveSetting } from "./settings-store";

const CSS = `
@keyframes dt-welcome-in {
  from { opacity: 0; transform: translateY(8px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.dt-welcome {
  position: fixed; inset: 0; z-index: 55; display: flex; align-items: center;
  justify-content: center; pointer-events: none;
  font: 13px/1.5 system-ui, -apple-system, sans-serif; color: #eceff6;
}
.dt-welcome .card {
  pointer-events: auto; width: 560px; max-width: calc(100vw - 32px);
  max-height: calc(100vh - 48px); overflow-y: auto;
  padding: 22px 24px; border-radius: 16px;
  display: flex; flex-direction: column; gap: 12px;
  background: linear-gradient(180deg, rgba(36,39,50,0.98), rgba(26,28,37,0.98));
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 18px 50px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.06) inset;
  animation: dt-welcome-in 0.18s cubic-bezier(0.16,1,0.3,1);
}
.dt-welcome h3 { margin: 0; font-size: 17px; font-weight: 650; letter-spacing: 0.01em; }
.dt-welcome p { margin: 0; color: #b9bfd0; }
.dt-welcome .cols { display: flex; gap: 20px; }
.dt-welcome .col { flex: 1 1 0; min-width: 0; }
.dt-welcome .sub {
  color: #8b90a3; font-size: 11px; text-transform: uppercase; letter-spacing: .05em;
  margin: 2px 0 6px;
}
.dt-welcome ul { margin: 0; padding: 0; list-style: none; }
.dt-welcome li { margin: 0 0 7px; color: #c7ccda; }
.dt-welcome li b { color: #e7e9f0; font-weight: 600; }
.dt-welcome kbd {
  font: 600 11px ui-monospace, SFMono-Regular, Menlo, monospace; color: #e7e9f0;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.16);
  border-bottom-width: 2px; border-radius: 5px; padding: 1px 6px; white-space: nowrap;
}
.dt-welcome .cam { color: #9aa0b4; font-size: 12px; }
.dt-welcome .row {
  display: flex; gap: 12px; justify-content: space-between; align-items: center;
  margin-top: 4px;
}
.dt-welcome .hint { color: #8b90a3; font-size: 12px; }
.dt-welcome .hint kbd { cursor: pointer; }
.dt-welcome button.primary {
  font: inherit; font-weight: 600; cursor: pointer; border-radius: 8px;
  padding: 7px 16px; border: 1px solid #6fa0ff; background: #6fa0ff; color: #11141c;
}
.dt-welcome button.primary:hover { background: #82b0ff; border-color: #82b0ff; }
@media (prefers-reduced-motion: reduce) { .dt-welcome .card { animation: none; } }
`;

// Key chip for a rebindable hotkey action, read live from ui/hotkeys (on first
// run these are the defaults, but a returning user who cleared only this flag
// still sees their own bindings).
function kbd(text: string): string {
  return `<kbd>${text}</kbd>`;
}
function actionKbd(id: string): string {
  const k = keyForAction(id);
  return k ? kbd(k.toUpperCase()) : "<i>unbound</i>";
}

let stylesInjected = false;
function injectStyles(): void {
  if (stylesInjected) return;
  const s = document.createElement("style");
  s.textContent = CSS;
  document.head.appendChild(s);
  stylesInjected = true;
}

// Only one welcome card at a time (first-run and "show again" share this).
let openRoot: HTMLElement | null = null;

// Install the welcome card. On first run (no persisted `welcomeSeen`) it shows
// immediately; the returned `show()` re-opens it on demand (the account menu's
// "Show welcome intro"), ignoring the persisted flag. `openHelp` opens the
// desktop help/shortcuts panel (ui/desktop-help.ts).
export function installWelcome(opts: { openHelp: () => void }): { show(): void } {
  if (!loadSettings().welcomeSeen) show();

  function show(): void {
    if (openRoot) return; // already showing
    injectStyles();

    const root = document.createElement("div");
    root.className = "dt-welcome";
    openRoot = root;
    const card = document.createElement("div");
    card.className = "card";

    const mod = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";
    card.innerHTML = `
      <h3>Welcome to TinyDraft</h3>
      <p>TinyDraft is a voxel editor and floor planner for desktop and VR:
      sculpt voxel <b>models</b> in Model mode, then draw walls and arrange your
      models into floor-plan <b>layouts</b> in Layout mode. Switch anytime with
      the <b>Layout / Model</b> toggle at the top of the sidebar.</p>
      <div class="cols">
        <div class="col">
          <div class="sub">Layout mode</div>
          <ul>
            <li><b>Draw walls</b> — Wall tool (${actionKbd("layout-wall")}),
              click corner to corner; ${kbd("Esc")} finishes.</li>
            <li><b>Doors &amp; windows</b> — select a wall
              (${actionKbd("tool-select")}), then Door / Window in the sidebar.</li>
            <li><b>Place models</b> — open the Models drawer at the bottom and
              click or drag a saved model into the plan.</li>
            <li><b>2D / 3D</b> — press ${actionKbd("view-toggle")} or use the
              2D / 3D buttons.</li>
          </ul>
        </div>
        <div class="col">
          <div class="sub">Model mode</div>
          <ul>
            <li><b>Draw voxels</b> — Create tool (${actionKbd("tool-create")}),
              drag on the canvas; pick a shape (Box, Extrude, …) in the sidebar.</li>
            <li><b>Erase / Paint</b> — ${actionKbd("tool-erase")} /
              ${actionKbd("tool-paint")}.</li>
            <li><b>Select</b> — ${actionKbd("tool-select")}, drag a rectangle or
              click an object.</li>
            <li><b>Undo</b> — ${kbd(`${mod} Z`)}; save with ${kbd(`${mod} S`)}.</li>
          </ul>
        </div>
      </div>
      <p class="cam"><b>Camera</b> — right-drag orbits, middle-drag pans, scroll
      zooms; in 3D views ${actionKbd("move-forward")} ${actionKbd("move-left")}
      ${actionKbd("move-back")} ${actionKbd("move-right")} fly and
      ${actionKbd("move-up")} / ${actionKbd("move-down")} rise / descend.</p>
    `;

    const row = document.createElement("div");
    row.className = "row";
    const hint = document.createElement("span");
    hint.className = "hint";
    hint.innerHTML = `Press ${kbd("?")} anytime for controls &amp; shortcuts.`;
    const go = document.createElement("button");
    go.className = "primary";
    go.textContent = "Get started";
    row.append(hint, go);
    card.appendChild(row);
    root.appendChild(card);
    document.body.appendChild(root);

    const dismiss = (): void => {
      saveSetting("welcomeSeen", true);
      root.remove();
      if (openRoot === root) openRoot = null;
      window.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey);
    };
    go.addEventListener("click", dismiss);
    // The ? chip in the footer opens help (and dismisses the welcome).
    hint.querySelector("kbd")?.addEventListener("click", () => {
      dismiss();
      opts.openHelp();
    });

    // First interaction outside the card dismisses — capture phase so it
    // observes the event, but never consumes it: the click/drag still does its job.
    const onPointerDown = (e: PointerEvent): void => {
      if (e.target instanceof Node && card.contains(e.target)) return;
      dismiss();
    };
    window.addEventListener("pointerdown", onPointerDown, true);

    // Esc / Enter dismiss (consumed, so nothing behind reacts — same bubble-phase
    // pattern as ui/modal.ts). Any other non-modifier key also dismisses but is
    // NOT consumed: the shortcut or camera key the user pressed still works.
    const MODIFIERS = new Set(["Shift", "Control", "Alt", "Meta", "CapsLock"]);
    const onKey = (e: KeyboardEvent): void => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (e.key === "Escape" || e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        dismiss();
      } else if (!MODIFIERS.has(e.key)) {
        dismiss();
      }
    };
    document.addEventListener("keydown", onKey);
  }

  return { show };
}
